/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsTriggerSuggestion } from "../funcs/skillsTriggerSuggestion.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  TriggerSkillSuggestionRequest,
  TriggerSkillSuggestionSecurity,
} from "../models/operations/triggerskillsuggestion.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type TriggerSkillSuggestionMutationVariables = {
  request: TriggerSkillSuggestionRequest;
  security?: TriggerSkillSuggestionSecurity | undefined;
  options?: RequestOptions;
};

export type TriggerSkillSuggestionMutationData = void;

export type TriggerSkillSuggestionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * triggerSuggestion skills
 *
 * @remarks
 * Manually run suggestion analysis for a skill, bypassing automatic feedback and efficacy thresholds while preserving the one-open-suggestion invariant.
 */
export function useTriggerSkillSuggestionMutation(
  options?: MutationHookOptions<
    TriggerSkillSuggestionMutationData,
    TriggerSkillSuggestionMutationError,
    TriggerSkillSuggestionMutationVariables
  >,
): UseMutationResult<
  TriggerSkillSuggestionMutationData,
  TriggerSkillSuggestionMutationError,
  TriggerSkillSuggestionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildTriggerSkillSuggestionMutation(client, options),
    ...options,
  });
}

export function mutationKeyTriggerSkillSuggestion(): MutationKey {
  return ["@gram/client", "skills", "triggerSuggestion"];
}

export function buildTriggerSkillSuggestionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: TriggerSkillSuggestionMutationVariables,
  ) => Promise<TriggerSkillSuggestionMutationData>;
} {
  return {
    mutationKey: mutationKeyTriggerSkillSuggestion(),
    mutationFn: function triggerSkillSuggestionMutationFn({
      request,
      security,
      options,
    }): Promise<TriggerSkillSuggestionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsTriggerSuggestion(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
